import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  TruckIcon, PlusIcon, EditIcon, Trash2Icon, WarehouseIcon, LockIcon, UnlockIcon,
  PackageOpenIcon, KeyRoundIcon, LinkIcon, RefreshCwIcon,
} from "lucide-react";

/* ===== Painel ADM: Fornecedores + Docas (Gestor) · Toggles exclusivos (Admin) ===== */

const inputCls = "mt-1 rounded-none bg-[#0A0A0C] border-border h-10";

export const FornecedoresAdmin = ({ isAdmin }) => {
  const [fornecedores, setFornecedores] = useState([]);
  const [docas, setDocas] = useState([]);
  const [settings, setSettings] = useState({ mod_fornecedores: true });
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nome: "", username: "", password: "" });
  const [novaDoca, setNovaDoca] = useState("");

  const load = async () => {
    const [f, d, s] = await Promise.all([
      api.get("/logistica/fornecedores").catch(() => ({ data: [] })),
      api.get("/logistica/docas").catch(() => ({ data: [] })),
      api.get("/logistica/settings").catch(() => ({ data: { mod_fornecedores: true } })),
    ]);
    setFornecedores(f.data); setDocas(d.data); setSettings(s.data);
  };
  useEffect(() => { load(); }, []);

  const salvar = async () => {
    if (!form.nome.trim()) return toast.error("Informe o nome do fornecedor");
    try {
      if (editing) {
        const payload = { nome: form.nome };
        if (form.password) payload.password = form.password;
        await api.patch(`/logistica/fornecedores/${editing.id}`, payload);
        toast.success("Fornecedor atualizado");
      } else {
        if (!form.username.trim() || !form.password) return toast.error("Informe usuário e senha de acesso");
        await api.post("/logistica/fornecedores", form);
        toast.success(`Fornecedor criado — acesso: ${form.username}`);
      }
      setModal(false); setEditing(null); setForm({ nome: "", username: "", password: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const excluir = async (f) => {
    if (!window.confirm(`Excluir fornecedor ${f.nome}? O acesso dele será revogado.`)) return;
    try { await api.delete(`/logistica/fornecedores/${f.id}`); toast.success("Fornecedor excluído"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleAtivo = async (f) => {
    try { await api.patch(`/logistica/fornecedores/${f.id}`, { ativo: !f.ativo }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleVazios = async (f) => {
    try {
      await api.patch(`/admin/fornecedores/${f.id}/vazios`, { enabled: !f.recebimento_vazios_enabled });
      toast.success(`Recebimento de Vazios ${!f.recebimento_vazios_enabled ? "LIGADO" : "DESLIGADO"} para ${f.nome}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleModulo = async (v) => {
    try {
      await api.patch("/admin/logistica/settings", { mod_fornecedores: v });
      setSettings({ ...settings, mod_fornecedores: v });
      toast.success(`Módulo de Fornecedores ${v ? "ATIVADO" : "DESATIVADO"} globalmente`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addDoca = async () => {
    if (!novaDoca.trim()) return;
    try { await api.post("/logistica/docas", { codigo: novaDoca }); setNovaDoca(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleDoca = async (d) => {
    try { await api.patch(`/logistica/docas/${d.id}`, { bloqueada: !d.bloqueada }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const copiarLink = async (f) => {
    if (!f.access_token) { toast.error("Fornecedor sem token — gere um novo link"); return; }
    const url = `${window.location.origin}/fornecedor/acesso/${f.access_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiado para a área de transferência");
    } catch {
      window.prompt("Copie o link de acesso do fornecedor:", url);
    }
  };

  const regenerarLink = async (f) => {
    if (!window.confirm(`Gerar novo link para ${f.nome}? O link anterior será revogado.`)) return;
    try {
      await api.post(`/logistica/fornecedores/${f.id}/token`);
      toast.success("Novo link gerado — o anterior foi revogado");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delDoca = async (d) => {
    if (!window.confirm(`Excluir doca ${d.codigo}?`)) return;
    try { await api.delete(`/logistica/docas/${d.id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="fornecedores-admin">
      {/* Toggle GLOBAL — exclusivo do Admin */}
      {isAdmin && (
        <Card className="bg-[#14151A] border-[#FF3B30]/30 rounded-none p-4 flex items-center justify-between" data-testid="toggle-modulo-fornecedores">
          <div>
            <div className="text-sm font-bold text-[#FF3B30] uppercase tracking-wider">Feature Toggle Global · Módulo Fornecedores</div>
            <div className="text-[11px] text-muted-foreground">Desligado: bloqueia login e portal de TODOS os fornecedores. Exclusivo do Admin.</div>
          </div>
          <Switch checked={settings.mod_fornecedores} onCheckedChange={toggleModulo} data-testid="switch-mod-fornecedores" />
        </Card>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {/* Fornecedores */}
        <Card className="bg-[#14151A] border-border rounded-none overflow-hidden">
          <div className="p-4 border-b border-border flex items-center gap-3">
            <TruckIcon className="w-4 h-4 text-[#FFD60A]" />
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#FFD60A]">Fornecedores ({fornecedores.length})</span>
            <Button size="sm" onClick={() => { setEditing(null); setForm({ nome: "", username: "", password: "" }); setModal(true); }} data-testid="btn-novo-fornecedor" className="ml-auto rounded-none bg-[#FFD60A] text-black hover:bg-[#ffdf3a] h-8 text-[10px] font-bold uppercase">
              <PlusIcon className="w-3 h-3 mr-1" /> Cadastrar
            </Button>
          </div>
          <div className="divide-y divide-border/50">
            {fornecedores.length === 0 && <div className="p-6 text-center text-xs text-muted-foreground">Nenhum fornecedor. Cadastre com usuário e senha de acesso direto.</div>}
            {fornecedores.map((f) => (
              <div key={f.id} className="p-3 flex items-center gap-3 flex-wrap" data-testid={`forn-${f.username}`}>
                <div className="flex-1 min-w-[160px]">
                  <div className="text-sm font-bold">{f.nome} {!f.ativo && <Badge className="rounded-none bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/40 text-[9px] ml-1">INATIVO</Badge>}</div>
                  <div className="text-[10px] font-mono text-muted-foreground flex items-center gap-1"><KeyRoundIcon className="w-3 h-3" /> {f.username} · base {f.base}</div>
                </div>
                <Button size="sm" onClick={() => copiarLink(f)} data-testid={`link-forn-${f.username}`} variant="outline" className="rounded-none h-7 text-[9px] font-bold uppercase border-[#00E5FF] text-[#00E5FF] hover:bg-[#00E5FF]/10">
                  <LinkIcon className="w-3 h-3 mr-1" /> Copiar Link
                </Button>
                <Button size="sm" variant="ghost" onClick={() => regenerarLink(f)} data-testid={`regen-forn-${f.username}`} title="Gerar novo link (revoga o anterior)" className="h-7 w-7 p-0 text-muted-foreground hover:text-[#00E5FF]">
                  <RefreshCwIcon className="w-3.5 h-3.5" />
                </Button>
                {isAdmin && (
                  <div className="flex items-center gap-2 border border-[#34C759]/30 bg-[#34C759]/5 px-2 py-1" data-testid={`vazios-toggle-${f.username}`}>
                    <PackageOpenIcon className="w-3.5 h-3.5 text-[#34C759]" />
                    <span className="text-[9px] uppercase font-bold text-[#34C759]">Receb. Vazios</span>
                    <Switch checked={!!f.recebimento_vazios_enabled} onCheckedChange={() => toggleVazios(f)} data-testid={`switch-vazios-${f.username}`} />
                  </div>
                )}
                <Switch checked={!!f.ativo} onCheckedChange={() => toggleAtivo(f)} data-testid={`switch-ativo-${f.username}`} />
                <Button size="sm" variant="ghost" onClick={() => { setEditing(f); setForm({ nome: f.nome, username: f.username, password: "" }); setModal(true); }} data-testid={`edit-forn-${f.username}`} className="h-7 w-7 p-0 text-muted-foreground hover:text-[#FFD60A]">
                  <EditIcon className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => excluir(f)} data-testid={`del-forn-${f.username}`} className="h-7 w-7 p-0 text-muted-foreground hover:text-[#FF3B30]">
                  <Trash2Icon className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </Card>

        {/* Docas */}
        <Card className="bg-[#14151A] border-border rounded-none overflow-hidden">
          <div className="p-4 border-b border-border flex items-center gap-3">
            <WarehouseIcon className="w-4 h-4 text-[#00E5FF]" />
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#00E5FF]">Docas de Descarregamento ({docas.length})</span>
            <div className="ml-auto flex gap-2">
              <Input value={novaDoca} onChange={(e) => setNovaDoca(e.target.value.toUpperCase())} placeholder="DC1" data-testid="input-nova-doca" className="rounded-none bg-[#0A0A0C] border-border h-8 w-24 font-mono text-xs" onKeyDown={(e) => e.key === "Enter" && addDoca()} />
              <Button size="sm" onClick={addDoca} data-testid="btn-add-doca" className="rounded-none bg-[#00E5FF] text-black hover:bg-[#00E5FF]/85 h-8 text-[10px] font-bold uppercase">
                <PlusIcon className="w-3 h-3" />
              </Button>
            </div>
          </div>
          <div className="p-4 flex flex-wrap gap-3">
            {docas.length === 0 && <div className="text-xs text-muted-foreground">Nenhuma doca. Crie DC1, DC2...</div>}
            {docas.map((d) => (
              <div key={d.id} className={`border p-3 flex items-center gap-3 ${d.bloqueada ? "border-[#FF3B30]/50 bg-[#FF3B30]/5" : "border-[#34C759]/50 bg-[#34C759]/5"}`} data-testid={`doca-${d.codigo}`}>
                <span className="font-mono font-black text-lg">{d.codigo}</span>
                <Badge className={`rounded-none border text-[9px] font-bold ${d.bloqueada ? "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/40" : "bg-[#34C759]/15 text-[#34C759] border-[#34C759]/40"}`}>
                  {d.bloqueada ? "BLOQUEADA" : "LIBERADA"}
                </Badge>
                <Button size="sm" variant="ghost" onClick={() => toggleDoca(d)} data-testid={`toggle-doca-${d.codigo}`} className="h-7 w-7 p-0 text-muted-foreground hover:text-white">
                  {d.bloqueada ? <UnlockIcon className="w-4 h-4" /> : <LockIcon className="w-4 h-4" />}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => delDoca(d)} data-testid={`del-doca-${d.codigo}`} className="h-7 w-7 p-0 text-muted-foreground hover:text-[#FF3B30]">
                  <Trash2Icon className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Modal cadastro/edição */}
      <Dialog open={modal} onOpenChange={(v) => { setModal(v); if (!v) setEditing(null); }}>
        <DialogContent className="bg-[#14151A] border-border rounded-none max-w-md" data-testid="modal-fornecedor">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wider">{editing ? "Editar Fornecedor" : "Cadastrar Fornecedor"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Nome / Razão Social</Label>
              <Input data-testid="forn-nome-input" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className={inputCls} />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Usuário de Acesso (sem e-mail)</Label>
              <Input data-testid="forn-username-input" value={form.username} disabled={!!editing} onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase().replace(/\s/g, "") })} className={`${inputCls} font-mono`} placeholder="quimica.alfa" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{editing ? "Nova Senha (deixe vazio p/ manter)" : "Senha"}</Label>
              <Input data-testid="forn-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={`${inputCls} font-mono`} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={salvar} data-testid="forn-salvar" className="rounded-none bg-[#FFD60A] text-black hover:bg-[#ffdf3a] font-bold uppercase tracking-wider">
              {editing ? "Salvar" : "Criar Acesso"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
